import {AxiosRequestConfig} from 'axios';
import {AccountService, EmailOtpVerifyResponse} from './account.service';
import Logger from '../../logger';
import {
  Account,
  Business,
  CreateReccoResourcePayload,
  Perk,
  Recco,
  Resource,
  Template,
  TextAnalysisResponse,
  EntityPageData,
  AccountSearchResult,
  AccountAuthDetails,
  PhoneNumber,
  Cred,
  AccountUpdatePayload,
  GiveReccoPayload,
  AuraWithCred,
} from '../../models';
import {getAuthToken} from '../../utils';
import {APIServiceImpl, ServiceResponse} from '../api';
import {LooseObject} from '../../types';
import {SKILL_TYPE, UserSkill} from '../../models/skill.model';
import {AccountOrganization} from '../../models/organization.model';
import {WorkExperience} from '../../models/work-experience.model';
import {EmailRegistrationParams} from './account-params';
import {EmailVerificationParams, ResendVerificationParams} from './account-verification-params';

export class AccountServiceImpl extends APIServiceImpl implements AccountService {
  static readonly SEARCH = '/accounts/search';
  static readonly CREATE = '/accounts';
  static readonly SEND_OTP = '/accounts/:accountId/otp';
  static readonly VERIFY_OTP = '/accounts/:accountId/verify';
  static readonly GET_RECCO = '/reccos/:reccoId';
  static readonly GET_CRED = '/creds/:credId';
  static readonly GET_CRED_RECCOS = '/accounts/:accountId/creds/:credId/reccos?pageNo=:pageNo&pageSize=:pageSize';
  static readonly SEARCH_RECCO = '/reccos/search?q=:searchTerm&page=:page&limit=:limit';
  static readonly TEXT_ANALYSIS = '/text-analysis';
  static readonly CREATE_RECCO_RESOURCE = '/accounts/:accountId/reccos/resources';
  static readonly CREATE_CRED_RESOURCE = '/accounts/:accountId/cred/resources';
  static readonly CREATE_RECCO = '/accounts/:accountId/creds/:credId/reccos';
  static readonly GET_ACCOUNT = '/accounts/:accountId';
  static readonly GET_ACCOUNT_BY_USERNAME = '/accounts/username/:username';
  static readonly GET_BUSINESS = '/accounts/:accountId/businesses/:businessId';
  static readonly GET_TEMPLATE = '/templates/:templateId';
  static readonly GET_PERK = '/businesses/:businessId/perks/:perkId';
  static readonly GET_HASHTAG_SUGGESTIONS = '/hashtags/search?q=:search&limit=:limit';
  static readonly GET_ENTITY_PAGE = '/entity-pages/web/?q=:search&limit=:limit&skip=:skip';
  static readonly UPDATE = '/accounts/:accountId';
  static readonly GET_RECCO_DISTRIBUTION_ANALYTIC = '/accounts/:accountId/recco-distribution-data';
  static readonly GET_AURAS_CRED = '/accounts/:accountId/creds/auras';
  static readonly FETCH_CREDS = '/accounts/:accountId/creds';
  static readonly GET_USER_SKILLS = 'accounts/:accountId/user-skills';
  static readonly GET_ACCOUNT_ORGANIZATIONS = 'accounts/:accountId/organizations';
  static readonly GET_WORK_EXPERIENCES = '/accounts/:accountId/organizations/:organizationId/work-experiences';
  static readonly GET_RECCOS = '/accounts/:accountId/reccos';
  static readonly REGISTER_BY_EMAIL = '/accounts';
  static readonly RESEND_VERIFICATION_EMAIL = '/accounts/:accountId/send-verification-email';
  static readonly VERIFY_EMAIL = '/accounts/:accountId/email/verify';
  static readonly VERIFY_EMAIL_OTP = '/accounts/:accountId/email/otp/verify';

  constructor() {
    super();
    this.service.interceptors.request.use((request): AxiosRequestConfig => {
      const token = getAuthToken();
      token && (request.headers.Authorization = `Bearer ${token}`);
      return request;
    });
  }

  async search(payload: (PhoneNumber | {email: string})[]): Promise<ServiceResponse<AccountSearchResult[]>> {
    try {
      const {data: res}: {data: any[]} = await this.post(AccountServiceImpl.SEARCH, payload);
      const results: AccountSearchResult[] = [];
      res?.forEach(resObj => results.push(new AccountSearchResult(resObj)));
      return new ServiceResponse<AccountSearchResult[]>(results);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(
        `POST ${AccountServiceImpl.SEARCH} ${JSON.stringify(payload)}: ${e.toString()}: ${error} ${JSON.stringify(
          e.response?.data?.failures || '',
        )}`,
      );
      return new ServiceResponse<AccountSearchResult[]>(undefined, error);
    }
  }

  async create(payload: PhoneNumber): Promise<ServiceResponse<Account>> {
    try {
      const {data: res} = await this.post(AccountServiceImpl.CREATE, payload);
      const account = new Account(res);
      return new ServiceResponse<Account>(account);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${AccountServiceImpl.CREATE} ${JSON.stringify(payload)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Account>(undefined, error);
    }
  }

  async sendOtp(accountId: string): Promise<ServiceResponse<boolean>> {
    try {
      const res = await this.post(AccountServiceImpl.SEND_OTP.replace(':accountId', accountId), {});
      return new ServiceResponse<boolean>(res?.status === 200);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${AccountServiceImpl.SEND_OTP} ${e.toString()}: ${error}`);
      return new ServiceResponse<boolean>(undefined, error);
    }
  }

  async verifyOtp(accountId: string, otp: string): Promise<ServiceResponse<AccountAuthDetails>> {
    try {
      const {data: res} = await this.post(AccountServiceImpl.VERIFY_OTP.replace(':accountId', accountId), {otp: otp});
      return new ServiceResponse<AccountAuthDetails>({
        id: accountId,
        token: res?.token,
      });
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.debug(`POST ${AccountServiceImpl.VERIFY_OTP.replace(':accountId', accountId)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<AccountAuthDetails>(undefined, error);
    }
  }

  async getRecco(reccoId: string): Promise<ServiceResponse<Recco>> {
    const url = AccountServiceImpl.GET_RECCO.replace(':reccoId', reccoId);
    try {
      const {data: res} = await this.get(url);
      const recco = new Recco(res);
      return new ServiceResponse<Recco>(recco);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: POST ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Recco>(undefined, error);
    }
  }

  async getCred(credId: string): Promise<ServiceResponse<Cred>> {
    const url = AccountServiceImpl.GET_CRED.replace(':credId', credId);
    try {
      const {data: res} = await this.get(url);
      const cred = new Cred(res);
      return new ServiceResponse<Cred>(cred);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: POST ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Cred>(undefined, error);
    }
  }

  async getCredReccos(credId: string): Promise<ServiceResponse<Recco[]>> {
    // TO DO - changed accordingly if needed
    // route for fetching reccos consists accountId but not used for any validation
    // and as user is not logged in so dummy id is used to match route to fetch reccos
    const url = AccountServiceImpl.GET_CRED_RECCOS.replace(':credId', credId)
      .replace(':accountId', credId)
      .replace(':pageNo', '1')
      .replace(':pageSize', '3');

    try {
      const {data: res} = await this.get(url);
      const reccos = res?.map((recco: any) => new Recco(recco));
      return new ServiceResponse<Recco[]>(reccos);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: POST ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Recco[]>(undefined, error);
    }
  }

  async textAnalysis(text: string): Promise<ServiceResponse<TextAnalysisResponse[]>> {
    const url = AccountServiceImpl.TEXT_ANALYSIS;
    try {
      const {data: res} = await this.post(url, {text});
      const results: TextAnalysisResponse[] = [];
      res?.forEach((resObj: any) => results.push(new TextAnalysisResponse(resObj)));
      return new ServiceResponse<TextAnalysisResponse[]>(results);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: POST ${url} "${text}": ${e.toString()}: ${error}`);
      return new ServiceResponse<TextAnalysisResponse[]>(undefined, error);
    }
  }

  async createReccoResource(
    accountId: string,
    payload: FormData | CreateReccoResourcePayload,
    onUploadProgress?: (progressEvent: ProgressEvent) => void,
  ): Promise<ServiceResponse<Resource[]>> {
    const url = AccountServiceImpl.CREATE_CRED_RESOURCE.replace(':accountId', accountId);
    try {
      const config: AxiosRequestConfig = {};
      if (onUploadProgress) {
        config.onUploadProgress = onUploadProgress;
      }
      const {data: res} = await this.post(url, payload, config);
      return new ServiceResponse<Resource[]>(res.map((re: any) => new Resource(re)));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: POST ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Resource[]>(undefined, error);
    }
  }

  async createRecco(accountId: string, credId: string, payload: GiveReccoPayload): Promise<ServiceResponse<Recco>> {
    const url = AccountServiceImpl.CREATE_RECCO.replace(':accountId', accountId).replace(':credId', credId);
    try {
      const {data: res} = await this.post(url, payload);
      const recco = new Recco(res);
      return new ServiceResponse<Recco>(recco);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: POST ${url} ${JSON.stringify(payload)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Recco>(undefined, error);
    }
  }

  async getAccount(accountId: string): Promise<ServiceResponse<Account>> {
    const url = AccountServiceImpl.GET_ACCOUNT.replace(':accountId', accountId);
    try {
      const {data: res} = await this.get(url);
      const account = new Account(res);
      return new ServiceResponse<Account>(account);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: GET ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Account>(undefined, error);
    }
  }

  async getAccountByUsername(username: string): Promise<ServiceResponse<Account>> {
    const url = AccountServiceImpl.GET_ACCOUNT_BY_USERNAME.replace(':username', encodeURIComponent(username));
    try {
      const {data: res} = await this.get(url);
      const account = new Account(res);
      return new ServiceResponse<Account>(account);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: GET ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Account>(undefined, error);
    }
  }

  async getBusiness(accountId: string, businessId: string): Promise<ServiceResponse<Business>> {
    const url = AccountServiceImpl.GET_BUSINESS.replace(':accountId', accountId).replace(':businessId', businessId);
    try {
      const {data: res} = await this.get(url);
      const business = new Business(res);
      return new ServiceResponse<Business>(business);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: GET ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Business>(undefined, error);
    }
  }

  async getTemplate(templateId: string): Promise<ServiceResponse<Template>> {
    const url = AccountServiceImpl.GET_TEMPLATE.replace(':templateId', templateId);
    try {
      const {data: res} = await this.get(url);
      const template = new Template(res);
      return new ServiceResponse<Template>(template);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: GET ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Template>(undefined, error);
    }
  }

  async getPerk(businessId: string, perkId: string): Promise<ServiceResponse<Perk>> {
    const url = AccountServiceImpl.GET_PERK.replace(':businessId', businessId).replace(':perkId', perkId);
    try {
      const {data: res} = await this.get(url);
      const perk = new Perk(res);
      return new ServiceResponse<Perk>(perk);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: GET ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Perk>(undefined, error);
    }
  }

  async getHashtagSuggestions(search: string, limit = 20): Promise<ServiceResponse<string[]>> {
    const url = AccountServiceImpl.GET_HASHTAG_SUGGESTIONS.replace(':search', search).replace(':limit', `${limit}`);
    try {
      const {data: res} = await this.get(url);
      return new ServiceResponse<string[]>(res);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: GET ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<string[]>(undefined, error);
    }
  }

  async getEntityPage(slug: string, search = '', skip = 0, limit = 20): Promise<ServiceResponse<EntityPageData>> {
    const url = AccountServiceImpl.GET_ENTITY_PAGE.replace(':search', search).replace(':limit', `${limit}`).replace(':skip', `${skip}`);
    try {
      const {data: res} = await this.post(url, {slug: slug});
      const entityPage = new EntityPageData(res);
      return new ServiceResponse<EntityPageData>(entityPage);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`Error: POST ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<EntityPageData>(undefined, error);
    }
  }

  async updateAccount(accountId: string, payload: AccountUpdatePayload): Promise<ServiceResponse<Account>> {
    try {
      const {data: res} = await this.patch(AccountServiceImpl.UPDATE.replace(':accountId', accountId), payload);
      const account = new Account(res);
      return new ServiceResponse<Account>(account);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`PATCH ${AccountServiceImpl.UPDATE.replace(':accountId', accountId)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Account>(undefined, error);
    }
  }

  async getReccoDistibutionAnalyticsData(accountId: string): Promise<ServiceResponse<LooseObject>> {
    try {
      const {data: res} = await this.get(AccountServiceImpl.GET_RECCO_DISTRIBUTION_ANALYTIC.replace(':accountId', accountId));
      return new ServiceResponse<LooseObject>(res);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${AccountServiceImpl.GET_RECCO_DISTRIBUTION_ANALYTIC} ${JSON.stringify(accountId)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<LooseObject>(undefined, error);
    }
  }

  async getCredsPerAuras(accountId: string): Promise<ServiceResponse<AuraWithCred[]>> {
    try {
      const {data: res} = await this.get(AccountServiceImpl.GET_AURAS_CRED.replace(':accountId', accountId));
      return new ServiceResponse<AuraWithCred[]>(res);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${AccountServiceImpl.GET_AURAS_CRED}: ${error}`);
      return new ServiceResponse<AuraWithCred[]>(undefined, error);
    }
  }

  async getCreds(
    accountId: string,
    organizationIds?: string[],
    pageNumber?: number,
    pageSize?: number,
    skillIds?: string[],
  ): Promise<ServiceResponse<Cred[]>> {
    const url = AccountServiceImpl.FETCH_CREDS.replace(':accountId', accountId);
    const params = {
      organizationIds: organizationIds?.join(','),
      pageNumber,
      pageSize,
      skillIds: skillIds?.join(','),
    };
    try {
      const {data: res} = await this.get(url, {params});
      const creds = res.map((cred: any) => new Cred(cred));
      return new ServiceResponse<Cred[]>(creds);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${url} with ${JSON.stringify(params)} : ${e.toString()}: ${error}`);
      return new ServiceResponse<Cred[]>(undefined, error);
    }
  }

  async getUserSkills(
    accountId: string,
    pageNumber: number = 1,
    pageSize: number = 10,
    type?: SKILL_TYPE,
  ): Promise<ServiceResponse<UserSkill[]>> {
    try {
      const {data: res} = await this.get(AccountServiceImpl.GET_USER_SKILLS.replace(':accountId', accountId), {
        params: {
          pageNumber,
          pageSize,
          type,
        },
      });
      return new ServiceResponse<UserSkill[]>(res?.map((item: any) => new UserSkill(item)));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${AccountServiceImpl.GET_USER_SKILLS} with ${pageNumber} and ${pageSize}: ${e.toString()}: ${error}`);
      return new ServiceResponse<UserSkill[]>(undefined, error);
    }
  }

  async getAccountOrganizations(
    accountId: string,
    pageNumber: number = 1,
    pageSize: number = 5,
  ): Promise<ServiceResponse<AccountOrganization[]>> {
    try {
      const {data} = await this.get(AccountServiceImpl.GET_ACCOUNT_ORGANIZATIONS.replace(':accountId', accountId), {
        params: {
          pageNumber,
          pageSize,
        },
      });
      return new ServiceResponse<AccountOrganization[]>(data?.map((item: any) => new AccountOrganization(item)));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${AccountServiceImpl.GET_ACCOUNT_ORGANIZATIONS} with ${pageNumber} and ${pageSize}: ${e.toString()}: ${error}`);
      return new ServiceResponse<AccountOrganization[]>(undefined, error);
    }
  }

  async getWorkExperiences(accountId: string, organizationId: string): Promise<ServiceResponse<WorkExperience[]>> {
    try {
      const {data} = await this.get(
        AccountServiceImpl.GET_WORK_EXPERIENCES.replace(':accountId', accountId).replace(':organizationId', organizationId),
      );
      return new ServiceResponse<WorkExperience[]>(data.map((item: any) => new WorkExperience(item)));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(
        `GET ${AccountServiceImpl.GET_WORK_EXPERIENCES.replace(':accountId', accountId).replace(
          ':organizationId',
          organizationId,
        )}: ${e.toString()}: ${error}`,
      );
      return new ServiceResponse<WorkExperience[]>(undefined, error);
    }
  }

  async getReccos(
    accountId: string,
    organizationIds?: string[],
    pageNumber?: number,
    pageSize?: number,
    skillIds?: string[],
  ): Promise<ServiceResponse<Recco[]>> {
    const url = AccountServiceImpl.GET_RECCOS.replace(':accountId', accountId);
    const params = {
      organizationIds: organizationIds?.join(','),
      pageNumber,
      pageSize,
      skillIds: skillIds?.join(','),
    };
    try {
      const {data: res} = await this.get(url, {params});
      const reccos = res.map((recco: any) => new Recco(recco));
      return new ServiceResponse<Recco[]>(reccos);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${url} with ${JSON.stringify(params)} : ${e.toString()}: ${error}`);
      return new ServiceResponse<Recco[]>(undefined, error);
    }
  }

  async registerByEmail(params: EmailRegistrationParams): Promise<ServiceResponse<Account>> {
    const url = AccountServiceImpl.REGISTER_BY_EMAIL;
    try {
      const {data: res} = await this.post(url, {
        firstName: params.firstName,
        lastName: params.lastName,
        email: params.email,
        status: params.status,
      });
      return new ServiceResponse<Account>(new Account(res));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${url} ${JSON.stringify(params)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Account>(undefined, error);
    }
  }

  async verifyEmail(params: EmailVerificationParams): Promise<ServiceResponse<boolean>> {
    const url = AccountServiceImpl.VERIFY_EMAIL.replace(':accountId', params.accountId);
    try {
      const {data: res} = await this.post(url, {token: params.token});
      const response = new ServiceResponse<boolean>(true);
      // Store the JWT token from response if available
      if (res?.token) {
        (response as any).token = res.token;
      }
      return response;
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${url} ${JSON.stringify({token: params.token})}: ${e.toString()}: ${error}`);
      return new ServiceResponse<boolean>(undefined, error);
    }
  }

  async resendVerificationEmail(params: ResendVerificationParams): Promise<ServiceResponse<boolean>> {
    const url = AccountServiceImpl.RESEND_VERIFICATION_EMAIL.replace(':accountId', params.accountId);
    try {
      await this.post(url, {});
      return new ServiceResponse<boolean>(true);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${url}: ${e.toString()}: ${error}`);
      return new ServiceResponse<boolean>(undefined, error);
    }
  }

  async verifyEmailOtp(accountId: string, token: string): Promise<ServiceResponse<EmailOtpVerifyResponse>> {
    const url = AccountServiceImpl.VERIFY_EMAIL_OTP.replace(':accountId', accountId);
    try {
      const {data: res} = await this.post(url, {token: String(token)});
      if (!res?.token) {
        return new ServiceResponse<EmailOtpVerifyResponse>(undefined, 'Invalid response from server.');
      }
      return new ServiceResponse<EmailOtpVerifyResponse>({token: res.token});
    } catch (e: unknown) {
      const responseData = (e as {response?: {data?: {failures?: Array<{field?: string; message?: string}>; message?: string}}})?.response
        ?.data;
      const error =
        responseData?.failures?.[0]?.message ??
        responseData?.message ??
        (e as Error).message ??
        (e as {response?: {status?: number}}).response?.status;
      Logger.error(`POST ${url}: ${String(e)}: ${error}`);
      return new ServiceResponse<EmailOtpVerifyResponse>(undefined, String(error));
    }
  }
}
